import { openAlertModal as unboundedOpenAlertModal } from 'admin/actions/alert-modal';
import {
  createBulkTransactions,
  createBulkTransactionsV2,
  createReward,
  fetchRewardReceivers,
  getBulkTransations,
  getRewardReceivers as unboundedGetRewardReceivers,
  initRewardReceivers as unboundedInitRewardReceivers,
  setUpdateReward as unboundedSetUpdateReward,
  updateReward,
} from 'admin/actions/rewards';
import ButtonGroup from 'admin/components/molecules/ButtonGroup';
import ApiResult from 'admin/components/shared/ApiResult';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import { formatNum, isEmptyObj } from 'admin/utils';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import CSSModules from 'react-css-modules';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import DateTime from '~/javascripts/admin/features/common/components/DateTime';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney, UserPrivateMoney } from '../../../reducers/user-private-money';
import Button from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Labeled from '../../atoms/Labeled';
import Loader from '../../atoms/Loader';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ClientIdsFileInputs from './ClientIdsFileInput';
import ReceiverListTable from './CustomerListTable';
import ReceiverFilterForm from './FilterForm';
import RewardSettingForm from './RewardSettingForm';
import RewardCSVUploader from './RewardSettingForm/RewardsCSVUploader';
import styles from './styles.scss';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center' as const,
  },
};

const buttonProps = [
  { text: 'リスト', value: 'userList', is_active: true },
  { text: 'CSVファイルから選択', value: 'userCSV', is_active: false },
];

export const TOPUP_METHODS = {
  direct: 'direct',
  collectable: 'collectable',
  bulk: 'bulk',
};

// crypto.randomUUID は secure context / 新しめのブラウザでのみ利用可能なため、
// 未対応環境ではフォールバックで 36 文字の v4 UUID を生成する
// (cf. assets/javascripts/web/storage.js)。
const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

// タイムアウト(504)/ゲートウェイエラー/ネットワーク断は「受付できたか不明」な曖昧状態。
// これらはサーバが受け付けている可能性があるため、失敗として扱わず一覧確認へ誘導する。
// (throwOnError は 504→'Gateway Timeout' / 502→'Bad Gateway' / 5xx→'Server Error' を throw、
//  ネットワーク断は fetch が TypeError で reject する)
const isAmbiguousAcceptanceError = (err: unknown): boolean =>
  err === 'Gateway Timeout' ||
  err === 'Bad Gateway' ||
  err === 'Server Error' ||
  err instanceof TypeError;

// 送信フェーズ:
//   idle       = 確認/入力
//   submitting = 送信中(閉じられない)
//   checking   = 曖昧エラー後、request_id で受付状況を照会中(閉じられない)
//   result     = 照会結果の案内表示(submitResult の内容で分岐)
// タイムアウト等の曖昧エラーは「元のエラー種別」ではなく、request_id で照会した
// 「最新の受付状況」で文言とフォーム挙動(維持/クリア)を決める。
type SubmitPhase = 'idle' | 'submitting' | 'checking' | 'result';

// 照会結果の案内文言。受付成立(=レコードあり)のときはフェーズ遷移時にフォームをクリア済み、
// 未達/判定不能のときはフォームを維持したまま案内する(いずれも「閉じる」は closeModal のみ)。
type SubmitResult = {
  message: string;
};

// bulk-transaction の「受付〜処理中」を表す status(一覧表示と揃える)。
// これらはまだ完了前だが、レコードが存在する=受付は成立している。
const IN_PROGRESS_BULK_STATUSES = [
  'receiving',
  'submitted',
  'examining',
  'queued',
  'processing',
  'scheduled',
];

//  Backward compatibility: Kept behind a flag (not deleted) so it can be restored immediately if a customer still needs it
const SHOW_SAVE_DRAFT = false;

type RewardShape = {
  id?: string;
  private_money?: UserPrivateMoney;
  money_amount?: number;
  point_amount?: number;
  point_expires_at?: string;
  point_expires_in_days?: number;
  sender_organization?: Record<string, unknown>;
  sender_shop?: Record<string, unknown>;
};

type Props = {
  reward?: RewardShape;
  receivers?: any;
  getRewardReceivers?: (query?: string) => void;
  setUpdateReward?: (reward: any) => void;
  initRewardReceivers?: () => void;
  openAlertModal?: (args: any) => void;
};

type RewardSettings = {
  privateMoney: UserPrivateMoney;
  senderOrganization: any;
  senderShop: any;
  topupMethod: string;
  moneyAmount: number;
  pointAmount: number;
  pointExpiryDate?: moment.Moment;
  relativePointExpiryDays?: number;
  useBearPointAccount: boolean;
  bearPointAccount: any;
  isReserve: boolean;
  reserveAt?: moment.Moment;
  description: string;
};

const SearchResultInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;
const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  margin-left: 15px;
  padding: 16px 0;
`;
const SearchResultCount = styled.span`
  flex: 1;
`;
const ModalConfirmTitle = styled.div`
  margin-bottom: 8px;
`;
const ModalConfirmInfo = styled.div`
  display: flex;
  border-bottom: solid 1px #e8e8e8;
  padding: 0 16px;
  margin-top: 4px;
`;
const ModalConfirmInfoTitle = styled.span``;
const ModalConfirmInfoDescription = styled.span`
  flex: 1;
  text-align: right;
  padding-right: 8px;
`;

const SubmittingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
`;
const TimeoutMessage = styled.p`
  text-align: left;
  line-height: 1.6;
  margin: 8px 0 16px;
`;

const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    margin-top: 10px;
    align-items: center;
    border-bottom: 1px solid #ddd;
    padding: 10px 15px;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
`;

const TopAlignedLabeled = styled(Labeled)`
  align-items: start !important;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const RadioItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SchedulingError = styled.div`
  color: ${({ theme }) => theme.colors.red[600]};
`;

const RewardsRegister = ({
  reward = {},
  receivers = [],
  getRewardReceivers = () => {},
  setUpdateReward = () => {},
  initRewardReceivers = () => {},
  openAlertModal = () => {},
}: Props) => {
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const allowedPrivateMoneys = user.issuedMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const history = useHistory();

  const {
    id: savedRewardId,
    private_money: savedPrivateMoney,
    money_amount: savedMoneyAmount,
    point_amount: savedPointAmount,
    point_expires_at: savedPointExpiresAt,
    point_expires_in_days: savedPointExpiresInDays,
    sender_organization: savedSenderOrganization,
    sender_shop: savedSenderShop,
  } = reward;

  const isSavedReward = !isEmptyObj(reward);

  const [rewardSettings, setRewardSettings] = useState<RewardSettings>({
    privateMoney: savedPrivateMoney || userPrivateMoney,
    senderOrganization: savedSenderOrganization || {},
    senderShop: savedSenderShop || {},
    topupMethod: TOPUP_METHODS.collectable,
    moneyAmount: savedMoneyAmount || 0,
    pointAmount: savedPointAmount || 0,
    pointExpiryDate: savedPointExpiresAt ? moment(savedPointExpiresAt) : undefined,
    relativePointExpiryDays: savedPointExpiresInDays || undefined,
    useBearPointAccount: false,
    bearPointAccount: {},
    isReserve: false,
    description: '',
  });

  const [receiverIds, setReceiverIds] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  // clear() 時にこの key を変えて RewardCSVUploader を再マウントし、
  // アップローダ内部の選択済みファイル表示(selectedFile)ごとリセットする。
  const [csvUploaderKey, setCsvUploaderKey] = useState<number>(0);
  const [willSchedule, setWillSchedule] = useState<boolean>(false);
  const [scheduledAt, setScheduledAt] = useState<moment.Moment | null>(
    moment().add(1, 'month').startOf('month').hour(10)
  );
  const [showSchedulingError, setShowSchedulingError] = useState<boolean>(false);

  // 二重登録防止: 送信セッション単位で冪等キー(request_id)を1つ払い出し、成功(clear)まで維持する。
  // 実行連打・確定の再オープン・エラー後リトライのいずれも同一 request_id を送り、サーバ側で
  // 冪等に処理(進行中は既存返却 / 失敗レコードは resume)させる。バッチ内容が変わったら振り直す(下の useEffect)。
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  // 送信中はモーダルを閉じられないローディングにし、二重送信を防ぐ。
  // 曖昧エラー時は checking→result フェーズで受付状況を照会し、結果に応じて案内する。
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);

  const [selectedCustomerInputButton, setSelectedCustomerInputButton] = useState<string>(
    buttonProps[0].value
  );
  const [apiResult, setApiResult] = useState({ message: '', isError: false });

  const [savedReceiversLoaded, setIsSavedReceiversLoaded] = useState(!isSavedReward);

  const filterRef = useRef<any>(null);

  useEffect(() => {
    if (isSavedReward) {
      getRewardReceivers(`messaging_operation_id=${savedRewardId}`);
    }

    return () => {
      setUpdateReward({});
      initRewardReceivers();
    };
  }, []);

  useEffect(() => {
    if (
      isSavedReward &&
      Array.isArray(receivers) &&
      receivers.length !== 0 &&
      !savedReceiversLoaded
    ) {
      setReceiverIds(receivers.map((r) => r.id));
      setIsSavedReceiversLoaded(true);
    }
  }, [receivers, savedReceiversLoaded]);

  // バッチ内容(送信対象/金額/説明など)が変わったら冪等キーを無効化し、次回開封時に振り直す。
  // (編集後の再送を古いレコードに dedup させないため。編集していない再送は同一キーで冪等/resume)
  useEffect(() => {
    setIdempotencyKey(null);
  }, [rewardSettings, receiverIds, csvData]);

  const onChangeReceiver = (e) => {
    const { value } = e.target;
    if (e.target.checked) {
      setReceiverIds([...receiverIds, value]);
    } else {
      setReceiverIds(receiverIds.filter((x) => x !== value));
    }
  };

  const onChangeSenderShop = (shop) => {
    setRewardSettings((settings) => ({ ...settings, senderShop: shop }));
    initRewardReceivers();
    // only clear if upload CSV is not selected.
    // because sometimes user might upload file first and select shop later
    if (selectedCustomerInputButton !== buttonProps[1].value) {
      setReceiverIds([]);
    }
    if (isEmptyObj(shop)) return;
    const { privateMoney } = rewardSettings;
    if (!isEmptyObj(privateMoney)) {
      getRewardReceivers(`private_money_id=${privateMoney.id}`);
    }
  };

  const buildPostData = () => {
    const {
      privateMoney,
      senderShop,
      topupMethod,
      moneyAmount,
      pointAmount,
      pointExpiryDate,
      relativePointExpiryDays,
      bearPointAccount,
      description,
    } = rewardSettings;

    // check related issue here: https://app.clickup.com/t/86enj0t0a
    const pointExpiryDateNextDay = pointExpiryDate
      ? moment(pointExpiryDate).add(1, 'days').startOf('day')
      : undefined;

    if (topupMethod === TOPUP_METHODS.direct) {
      return {
        request_id: idempotencyKey ?? undefined,
        sender_id: senderShop.id,
        receiver_ids: receiverIds,
        private_money_id: privateMoney.id,
        money_amount: moneyAmount,
        point_amount: pointAmount,
        description,
        point_expires_at: pointExpiryDateNextDay,
        point_expires_in_days: relativePointExpiryDays,
        // 負担先店舗が選択されていれば送る(bulk 方式と判定式を揃える)。
        // useBearPointAccount フラグは true に切り替わる箇所がなく常に false のため、
        // 従来はここが常に undefined になり、選択した負担先店舗が送信されていなかった。
        bear_point_user_id: !isEmptyObj(bearPointAccount) ? bearPointAccount.id : undefined,
        scheduled_at: willSchedule ? scheduledAt?.toISOString() ?? undefined : undefined,
      };
    }

    if (topupMethod === TOPUP_METHODS.bulk) {
      return {
        request_id: idempotencyKey ?? undefined,
        sender_id: senderShop.id,
        receiver_ids: csvData.map((row) => row.customer_id),
        private_money_id: privateMoney.id,
        description,
        name: description,
        money_amount_list: csvData.map((row) => row.money_amount),
        point_amount_list: csvData.map((row) => row.point_amount),
        description_list: csvData.map((row) => row.description || ''),
        point_expires_at: pointExpiryDateNextDay,
        point_expires_in_days: relativePointExpiryDays,
        bear_point_user_id: !isEmptyObj(bearPointAccount) ? bearPointAccount.id : undefined,
        scheduled_at: willSchedule ? scheduledAt?.toISOString() ?? undefined : undefined,
      };
    }

    return {
      private_money_id: privateMoney.id,
      sender_id: senderShop.id,
      ids: receiverIds,
      money_amount: moneyAmount,
      point_amount: pointAmount,
      reserve_at: willSchedule ? scheduledAt?.toISOString() ?? '' : '',
      point_expires_at: isSavedReward ? undefined : pointExpiryDateNextDay,
      point_expires_in_days: isSavedReward ? undefined : relativePointExpiryDays,
    };
  };

  // フォーム入力・受信者・冪等キー等を初期化する(モーダルの開閉/フェーズには触れない)。
  // 受付が成立した(=レコードが存在する)ケースで、結果案内モーダルを開いたまま
  // 裏でフォームだけをクリアするために clear() から分離している。
  const resetFormFields = () => {
    const currentTopupMethod = rewardSettings.topupMethod;
    setRewardSettings({
      ...rewardSettings,
      privateMoney: userPrivateMoney,
      senderOrganization: {},
      senderShop: {},
      topupMethod: currentTopupMethod,
      moneyAmount: 0,
      pointAmount: 0,
      pointExpiryDate: undefined,
      relativePointExpiryDays: undefined,
      useBearPointAccount: false,
      bearPointAccount: {},
      isReserve: false,
      description: '',
    });

    setUpdateReward({});
    setReceiverIds([]);
    setCsvData([]);
    // CSVアップローダを再マウントして選択済みファイル表示もクリアする
    setCsvUploaderKey((k) => k + 1);
    setIdempotencyKey(null);
    initRewardReceivers();
    setSelectedFile(null);
    // eslint-disable-next-line no-unused-expressions
    filterRef.current?.onClear();
    setQueryParams('');
  };

  const clear = () => {
    resetFormFields();
    setSubmitPhase('idle');
    setIsModalOpen(false);
  };

  const flashApiResult = (message = '成功しました', isError = false) => {
    setApiResult({ message, isError });
    setTimeout(() => setApiResult({ message: '', isError: false }), 10000);
  };

  const handleError = (err) => {
    switch (err.type) {
      case 'messaging_operation_over_transfer_limit':
        flashApiResult('送金上限を超えています', true);
        break;
      case 'account_can_not_topup':
        flashApiResult(err.message, true);
        break;
      default:
        flashApiResult('システムエラーが発生しました。システム管理者にお問い合わせ下さい', true);
        console.error(err.message);
        break;
    }
  };

  const openModal = () => {
    // 既存キーがあれば維持し、未発番のときだけ払い出す(再オープンで request_id を変えない)
    setIdempotencyKey((k) => k ?? generateRequestId());
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const saveCollectableReward = (isDraft = false) => {
    const postData = { ...buildPostData(), is_draft: isDraft };
    const apiCall = isSavedReward
      ? () => updateReward(savedRewardId as string, postData)
      : () => createReward(postData);
    return apiCall()
      .then(() => {
        clear();
        flashApiResult();
      })
      .catch((err) => {
        Promise.resolve(err).then((e) => handleError(e));
      });
  };

  const validateScheduling = () => {
    const minScheduleTime = moment().add(10, 'minute');
    if (willSchedule && minScheduleTime.isSameOrAfter(scheduledAt)) {
      setShowSchedulingError(true);
      return false;
    }
    setShowSchedulingError(false);
    return true;
  };

  const onConfirmDraft = () => {
    if (rewardSettings.topupMethod !== TOPUP_METHODS.collectable) return;
    saveCollectableReward(true);
  };

  // 曖昧エラー(504/502/5xx/通信断)後、ユーザに代わって request_id で受付状況を照会し、
  // 最新状況に応じた案内へ遷移する。レコードがあれば受付成立とみなしフォームをクリア、
  // 無ければ未達としてフォームを維持(再実行可能)、照会自体が失敗したら判定不能で維持する。
  const resolveAmbiguousAcceptance = (requestId: string | null) => {
    setSubmitPhase('checking');

    if (!requestId) {
      // request_id 未発番(通常は起きない)。照会できないため判定不能扱いでフォームを維持する。
      setSubmitResult({
        message:
          '受付状況を確認できませんでした。一覧でご確認のうえ、必要に応じて再実行してください。',
      });
      setSubmitPhase('result');
      return;
    }

    getBulkTransations(`request_id=${encodeURIComponent(requestId)}`)
      .then((res: any) => {
        const bulkTransactions = res?.rows ?? [];

        if (bulkTransactions.length === 0) {
          // レコードなし=サーバ未達(接続タイムアウト等)。フォームを維持し再実行を促す。
          setSubmitResult({
            message: '送信は完了していません。お手数ですが、もう一度実行してください。',
          });
          setSubmitPhase('result');
          return;
        }

        // レコードあり=受付成立。二重付与を避けるためフォームをクリアし一覧へ誘導する。
        const status: string = bulkTransactions[0]?.status;
        let message: string;
        if (status === 'error') {
          message = '受付後にエラーが発生しました。一覧で内容をご確認ください。';
        } else if (status === 'done') {
          message = '実行が完了しました。一覧で結果をご確認ください。';
        } else if (IN_PROGRESS_BULK_STATUSES.includes(status)) {
          message = '受付が完了しました。処理は順次行われます。一覧で状況をご確認ください。';
        } else {
          // 想定外の status。受付レコードは存在するため安全側で一覧確認へ誘導する。
          message = '受付が完了しました。一覧で状況をご確認ください。';
        }
        resetFormFields();
        setSubmitResult({ message });
        setSubmitPhase('result');
      })
      .catch(() => {
        // 照会自体も失敗(通信状況が悪い等)。判定不能のため安全側でフォームを維持する。
        setSubmitResult({
          message:
            '受付状況を確認できませんでした。一覧でご確認のうえ、必要に応じて再実行してください。',
        });
        setSubmitPhase('result');
      });
  };

  const onConfirm = () => {
    // 「実行」連打による二重送信を防ぐ。送信中はモーダルを閉じられないローディングにする。
    // NOTE: リロードしても再送信されない。onConfirm はユーザー操作でのみ呼ばれ、マウント時の
    //       自動送信は無い。fetch はネイティブ form POST ではないため「フォーム再送信」も出ない。
    if (submitPhase === 'submitting') return;
    setSubmitPhase('submitting');

    const { topupMethod } = rewardSettings;
    // 送信した冪等キー。曖昧エラー時の受付状況照会(request_id 検索)に使う。
    const requestId = idempotencyKey;

    // collectable(付与予約/下書き系)は bulk-transaction ではないため一覧誘導は行わず、
    // ローディング表示のみ適用して従来どおり saveCollectableReward に処理を委譲する。
    if (topupMethod === TOPUP_METHODS.collectable) {
      saveCollectableReward().finally(() => {
        setSubmitPhase('idle');
        setIsModalOpen(false);
      });
      return;
    }

    const apiCall =
      topupMethod === TOPUP_METHODS.direct ? createBulkTransactions : createBulkTransactionsV2;

    apiCall(buildPostData())
      .then(() => {
        clear();
        flashApiResult();
        setSubmitPhase('idle');
        if (willSchedule) {
          history.push(`/promotion/rewards?tab=1`);
        }
      })
      .catch((err) => {
        if (isAmbiguousAcceptanceError(err)) {
          // 受付できたか不明。失敗表示にせず、request_id で受付状況を照会して案内を出し分ける。
          resolveAmbiguousAcceptance(requestId);
        } else {
          // ドメインエラー(バリデーション等): 明確な失敗。閉じて内容修正を促す。
          setSubmitPhase('idle');
          setIsModalOpen(false);
          Promise.resolve(err).then((e) => handleError(e));
        }
      });
  };

  const onCheckAll = (checked) => {
    if (checked) {
      const { count: length } = receivers;
      const { privateMoney } = rewardSettings;
      const query = queryParams.length > 0 ? queryParams : `private_money_id=${privateMoney.id}`;
      fetchRewardReceivers(`${query}&per_page=${length}`).then((c: any) => {
        setReceiverIds(c.rows.map((x) => x.id));
      });
    } else {
      setReceiverIds([]);
    }
  };

  const onFilterSubmit = (query) => {
    setQueryParams(query);
    getRewardReceivers(query);
    setReceiverIds([]);
  };

  const onChangePage = (page) => {
    const { privateMoney } = rewardSettings;
    const query = queryParams.length > 0 ? queryParams : `private_money_id=${privateMoney.id}`;
    getRewardReceivers(`${query}&page=${page}&per_page=50`);
  };

  const isFormValid = () => {
    const {
      privateMoney,
      moneyAmount,
      pointAmount,
      isReserve,
      reserveAt,
      senderShop,
      topupMethod,
      useBearPointAccount,
      bearPointAccount,
    } = rewardSettings;

    if (Object.keys(senderShop).length === 0) return false;

    if (topupMethod === TOPUP_METHODS.bulk) {
      if (csvData.length === 0) return false;
    } else {
      if (moneyAmount === 0 && pointAmount === 0) return false;
      if (receiverIds.length === 0) return false;
      if (
        privateMoney &&
        privateMoney.transfer_limit != null &&
        moneyAmount > privateMoney.transfer_limit
      )
        return false;
    }

    if (isReserve && reserveAt && reserveAt < moment()) return false;
    if (useBearPointAccount && isEmptyObj(bearPointAccount)) return false;

    if (willSchedule && !scheduledAt) {
      return false;
    }

    return true;
  };

  const showInvalidCSVError = () => {
    openAlertModal({
      body: 'CSV ファイルが無効です',
      useCancel: false,
    });
  };

  const getTableData = () => {
    if (Array.isArray(receivers)) {
      return {
        rows: receivers,
        count: receivers.length,
        pagination: null,
      };
    }
    return receivers;
  };

  const { rows: customers, count: length, pagination } = getTableData();

  const crumb = [
    { value: 'ホーム', href: '/', active: false },
    { value: '販促管理', href: '', active: true },
    { value: 'マネー/ポイント付与管理', href: '/promotion/rewards', active: false },
    { value: 'マネー/ポイント付与新規作成', href: '', active: true },
  ];

  const totalReceiverCount =
    rewardSettings.topupMethod === TOPUP_METHODS.bulk ? csvData.length : receiverIds.length;

  const totalMoneyAmount =
    rewardSettings.topupMethod === TOPUP_METHODS.bulk
      ? csvData.reduce((acc, cur) => acc + cur.money_amount, 0)
      : receiverIds.length * rewardSettings.moneyAmount;

  const totalPointAmount =
    rewardSettings.topupMethod === TOPUP_METHODS.bulk
      ? csvData.reduce((acc, cur) => acc + cur.point_amount, 0)
      : receiverIds.length * rewardSettings.pointAmount;

  return (
    <div style={{ marginBottom: '320px' }}>
      <Breadcrumb items={crumb} />
      {!isSelectedMoneyAllowed && (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
      {isSelectedMoneyAllowed && (
        <>
          <div styleName="headline">基本設定</div>
          <RewardSettingForm
            rewardSettings={rewardSettings}
            setRewardSettings={setRewardSettings}
            isSavedReward={isSavedReward}
            onChangeSenderShop={onChangeSenderShop}
          />
          {rewardSettings.topupMethod === TOPUP_METHODS.bulk ? (
            <>
              <div styleName="headline" style={{ marginTop: '1.25rem' }}>
                CSVファイル設定 <Required />
              </div>
              <RewardCSVUploader key={csvUploaderKey} onCSVDataChange={setCsvData} />
            </>
          ) : (
            <>
              <div styleName="headline">付与先ユーザ設定</div>
              <div styleName="userswitch">
                <ButtonGroup
                  buttons={buttonProps.map((btn) => ({
                    ...btn,
                    is_active: btn.value === selectedCustomerInputButton,
                  }))}
                  onClick={(__, index) => {
                    const values = buttonProps.map((x) => x.value);
                    setSelectedCustomerInputButton(values[index]);
                    setSelectedFile(null);
                    setReceiverIds([]);
                  }}
                />
              </div>
              {selectedCustomerInputButton === buttonProps[0].value && (
                <>
                  <ReceiverFilterForm
                    ref={filterRef}
                    onFilterSubmit={onFilterSubmit}
                    isMoneySelected={Object.keys(rewardSettings.privateMoney).length !== 0}
                    selectedMoney={rewardSettings.privateMoney}
                  />
                  <SearchResultInfo>
                    <SearchResultTitle>検索結果</SearchResultTitle>
                    <SearchResultCount>件数：{length ?? 0}</SearchResultCount>
                  </SearchResultInfo>
                  <ReceiverListTable
                    receivers={customers}
                    onChangeCheckbox={onChangeReceiver}
                    onCheckAll={onCheckAll}
                    checkedIds={receiverIds}
                    pagination={pagination}
                    onChangePage={onChangePage}
                  />
                </>
              )}
              {selectedCustomerInputButton === buttonProps[1].value && (
                <ClientIdsFileInputs
                  onReadCustomerIds={setReceiverIds}
                  onError={showInvalidCSVError}
                  selectedFile={selectedFile}
                  onSelectFile={(file) => setSelectedFile(file)}
                />
              )}
            </>
          )}

          <div styleName="headline">実行予約設定</div>
          <FormWrapper>
            <TopAlignedLabeled labelText="実行予約">
              <RadioWrapper>
                <RadioItem>
                  <Radio
                    name="scheduleOption"
                    checked={!willSchedule}
                    onChange={(value) => value && setWillSchedule(false)}
                  />
                  <span>しない</span>
                </RadioItem>
                <RadioItem>
                  <Radio
                    name="scheduleOption"
                    checked={willSchedule}
                    onChange={(value) => value && setWillSchedule(true)}
                  />
                  <DateTime
                    isDisabled={!willSchedule}
                    isValidDate={(date) => moment().startOf('day').isSameOrBefore(date)}
                    value={scheduledAt}
                    onChange={(value) => setScheduledAt(value)}
                  />
                </RadioItem>
                {showSchedulingError && (
                  <SchedulingError>現時点より10分以上先の時刻を指定してください</SchedulingError>
                )}
              </RadioWrapper>
            </TopAlignedLabeled>
          </FormWrapper>

          <ApiResult message={apiResult.message} isError={apiResult.isError} />
          <div styleName="button-group">
            {SHOW_SAVE_DRAFT && (
              <Button
                onClick={onConfirmDraft}
                disabled={
                  rewardSettings.topupMethod !== TOPUP_METHODS.collectable || !isFormValid()
                }
                variant="muted-outline"
              >
                下書き保存
              </Button>
            )}
            <Button
              onClick={() => validateScheduling() && openModal()}
              disabled={!isFormValid() || submitPhase !== 'idle'}
            >
              確定
            </Button>
            <Modal
              isOpen={isModalOpen}
              onRequestClose={() => {
                // 送信中・照会中・結果案内中は閉じさせない(誤って閉じて進行中を見失うのを防ぐ)
                if (submitPhase === 'idle') closeModal();
              }}
              // 閉じ切ってからフェーズ/結果をリセットする。フェードアウト中に確認モーダル(idle)へ
              // 切り替わって「実行ボタン」が一瞬見える(案内と矛盾)のを防ぐ。
              onAfterClose={() => {
                setSubmitPhase('idle');
                setSubmitResult(null);
              }}
              shouldCloseOnOverlayClick={submitPhase === 'idle'}
              shouldCloseOnEsc={submitPhase === 'idle'}
              closeTimeoutMS={200}
              ariaHideApp={false}
              style={modalStyles}
            >
              {submitPhase === 'submitting' && (
                <SubmittingWrapper>
                  <Loader type="Oval" width={40} height={40} />
                  <div>送信中です。この画面を閉じずにお待ちください。</div>
                </SubmittingWrapper>
              )}

              {submitPhase === 'checking' && (
                <SubmittingWrapper>
                  <Loader type="Oval" width={40} height={40} />
                  <div>受付状況を確認しています。この画面を閉じずにお待ちください。</div>
                </SubmittingWrapper>
              )}

              {submitPhase === 'result' && submitResult && (
                <div>
                  <ModalConfirmTitle>受付状況</ModalConfirmTitle>
                  <TimeoutMessage>{submitResult.message}</TimeoutMessage>
                  <div styleName="button-group">
                    <Button onClick={closeModal} variant="neutral">
                      閉じる
                    </Button>
                    <Button onClick={() => history.push(`/promotion/rewards?tab=1`)}>
                      一覧で確認する
                    </Button>
                  </div>
                </div>
              )}

              {/*
                isModalOpen も条件に含める。react-modal は closeTimeoutMS の間フェードアウトしても
                子要素を描画し続けるため、成功時に clear() で submitPhase=idle・isModalOpen=false と
                なった直後の閉じアニメ中に、この確認モーダル(実行ボタン)が一瞬再表示され押せてしまう
                (成功済みなのに再実行→二重付与/システムエラー)。isModalOpen=false の間は出さない。
              */}
              {isModalOpen && submitPhase === 'idle' && (
                <>
                  <ModalConfirmTitle>以下の内容で実行しますか？</ModalConfirmTitle>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>送信元店舗</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>
                      {rewardSettings.senderShop?.name ?? '-'}
                    </ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>対象マネー</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>
                      {rewardSettings.privateMoney?.name ?? '-'}
                    </ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>ユーザ数</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>{totalReceiverCount}</ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>マネー総額</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>
                      {formatNum(totalMoneyAmount)}
                    </ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>ポイント総額</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>
                      {formatNum(totalPointAmount)}
                    </ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <ModalConfirmInfo>
                    <ModalConfirmInfoTitle>実行予定</ModalConfirmInfoTitle>
                    <ModalConfirmInfoDescription>
                      {willSchedule && scheduledAt
                        ? scheduledAt.format('YYYY/MM/DD HH:mm')
                        : '即時'}
                    </ModalConfirmInfoDescription>
                  </ModalConfirmInfo>
                  <div styleName="button-group">
                    <Button onClick={closeModal} variant="neutral">
                      キャンセル
                    </Button>
                    <Button onClick={onConfirm}>実行</Button>
                  </div>
                </>
              )}
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  reward: state.reward.reward,
  receivers: state.reward.receivers,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getRewardReceivers: unboundedGetRewardReceivers,
      initRewardReceivers: unboundedInitRewardReceivers,
      setUpdateReward: unboundedSetUpdateReward,
      openAlertModal: unboundedOpenAlertModal,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CSSModules(RewardsRegister, styles, { allowMultiple: true }));
